import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { ROLE_LABEL, Shell } from "@/app/Shell";
import { useMe } from "@/app/session";
import { RequestError } from "@/app/queries";
import { useAssignment, useWorkspace, submissionExportUrl } from "@/app/notebookQueries";
import { useAddComment, useMySubmissions, useSubmission, type SubmissionBrief, type SubmissionView } from "@/app/reviewQueries";
import type { Notebook, Task } from "@/kernel/runner";
import { count } from "@/lib/plural";
import { StatusChip } from "@/ui/StatusChip";
import { EmptyState, NotFound, SkeletonRows, StateCard } from "@/ui/states";
import { AllComments, AutochecksCard, HistoryCard } from "./submission/Aside";
import { StudentCells } from "./submission/Cells";
import { cellRows, fileName, fmtWhen, latestId, runScore, STATUS_CHIP } from "./submission/model";
import { StudentVerdict } from "./submission/StudentVerdict";
import { Versions } from "./submission/Versions";

type User = { name: string; role: string } | undefined;

/** S7 «Сдача и ревью» глазами студента: версии, вердикт и перечень правок из
 *  комментариев, треды у ячеек (студент только отвечает), «исправить и отправить vN+1». */
export function SubmissionPage({ assignmentId, submissionId }: { assignmentId: string; submissionId?: string }) {
  const me = useMe();
  const user: User = me.data ? { name: me.data.name, role: ROLE_LABEL[me.data.role] ?? me.data.role } : undefined;
  const list = useMySubmissions(assignmentId);
  const current = latestId(list.data, submissionId);
  const sub = useSubmission(current);
  const frame = (children: ReactNode) => (
    <Shell nav user={user}>
      {children}
    </Shell>
  );
  const err = list.error ?? sub.error;
  if (err instanceof RequestError && err.code === "step_locked") return frame(<StateCard code="LOCKED" where="сдача" title="Шаг ещё закрыт" body={err.message} primary={{ label: "вернуться к маршруту", href: "/" }} />);
  if (err instanceof RequestError && err.status === 404) return frame(<NotFound primary={{ label: "к ноутбуку", href: `/notebook/${assignmentId}` }} />);
  if (err)
    return frame(
      <StateCard
        code="ERR_NETWORK"
        where="сдача"
        tone="warn"
        title="Нет связи с сервером"
        body="Сдача и комментарии не загрузились. Они хранятся на сервере — ничего не потеряно."
        primary={{ label: "повторить сейчас", onClick: () => (void list.refetch(), void sub.refetch()) }}
      />,
    );
  if (!list.data) return frame(<SkeletonRows />);
  if (list.data.length === 0)
    return frame(
      <EmptyState
        where="домашка · сдачи"
        title="Сдач ещё нет"
        marks={[true, false, false]}
        body="Сдача появится здесь после «отправить на проверку» в ноутбуке: ментор прокомментирует конкретные ячейки, а вердикт и перечень правок соберутся из комментариев."
        primary={{ label: "открыть ноутбук", href: `/notebook/${assignmentId}` }}
      />,
    );
  if (!sub.data) return frame(<SkeletonRows />);
  return <Submission view={sub.data} list={list.data} assignmentId={assignmentId} me={me.data?.name ?? "студент"} user={user} />;
}

function Submission({ view, list, assignmentId, me, user }: { view: SubmissionView; list: SubmissionBrief[]; assignmentId: string; me: string; user: User }) {
  const asg = useAssignment(assignmentId);
  const ws = useWorkspace(assignmentId);
  const tasks = (asg.data?.manifest.tasks ?? []) as Task[];
  const nb = ws.data?.notebook as Notebook | undefined;
  const addComment = useAddComment(view.id);
  const rows = cellRows(view, tasks).filter((r) => r.threads.length > 0);
  const score = runScore(view);
  const latest = list[0]?.id === view.id;
  const file = asg.data?.file_name ?? fileName(view.assignment);
  const total = score?.total ?? tasks.length;

  const reply = async (parentId: string, cellId: string, taskN: number | undefined, body: string) => {
    await addComment.mutateAsync({ parent_id: parentId, cell_id: cellId, task_n: taskN, body });
  };

  const mentorMeta =
    view.status === "on_review"
      ? view.reviewer
        ? `ментор ${view.reviewer.name} · смотрит`
        : "ждёт ментора"
      : `ментор ${view.reviewer?.name ?? ""}${view.verdict_at ? ` · ответил ${fmtWhen(view.verdict_at)}` : ""}`;

  return (
    <Shell wide user={user}>
      <div className="flex flex-wrap items-center gap-3 border-b border-bd px-5 py-2.5">
        <span className="font-mono text-[12px] text-mut" data-testid="breadcrumb">
          <Link to="/homework" className="text-mut no-underline hover:text-ink">
            домашки
          </Link>{" "}
          /{" "}
          <Link to="/notebook/$assignmentId" params={{ assignmentId }} className="text-mut no-underline hover:text-ink">
            {file}
          </Link>{" "}
          / сдача v{view.iteration}
        </span>
        <StatusChip status={STATUS_CHIP[view.status]} compact />
        {!view.run_id && (
          <span className="font-mono text-[11px] text-warn" data-testid="no-autocheck">
            без автопроверки
          </span>
        )}
        <a href={submissionExportUrl(view.id)} download className="font-mono text-[11px] text-acc no-underline underline-offset-2 hover:underline" data-testid="download-version">
          скачать v{view.iteration} .ipynb
        </a>
        <span className="flex-1" />
        <span className="font-mono text-[11.5px] text-mut" data-testid="mentor-meta">
          {mentorMeta}
        </span>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-[236px_minmax(0,1fr)_372px]">
        <aside className="border-bd lg:border-r">
          <Versions assignmentId={assignmentId} list={list} current={view} />
        </aside>
        <div className="flex flex-col gap-3.5 px-[22px] pb-10 pt-5">
          <div className="flex flex-wrap items-center gap-3.5">
            <h1 className="m-0 font-mono text-[18px] font-bold tracking-[-.015em]">{file}</h1>
            <span className="text-[12.5px] text-mut">
              {count(total, "задача", "задачи", "задач")}
              {asg.data ? ` · ${asg.data.track_slug} → ${asg.data.step_title}` : ""}
            </span>
            <span className="flex-1" />
            {score && (
              <span className="flex items-center gap-2.5">
                <span className="flex gap-[2px]" aria-hidden>
                  {score.cells.map((st, i) => (
                    <span key={i} className="size-[9px] rounded-[2px]" style={{ background: st === "passed" ? "var(--acc)" : st === "failed" ? "var(--err)" : "transparent", border: `1px solid ${st === "passed" ? "var(--acc)" : st === "failed" ? "var(--err)" : "var(--bd2)"}` }} />
                  ))}
                </span>
                <span className="font-mono text-[11.5px]" style={{ color: score.passed === score.total ? "var(--ok)" : "var(--err)" }}>
                  {score.passed}/{score.total} автопроверок
                </span>
              </span>
            )}
          </div>
          {rows.length === 0 ? (
            <p className="m-0 rounded-md border border-bd bg-card px-4 py-3.5 text-[13px] leading-[1.55] text-ink2">
              {view.status === "on_review" ? "Комментариев пока нет — они появятся у ячеек, когда ментор посмотрит работу." : "Комментариев к ячейкам нет — ментор оставил только сводку в вердикте."}
            </p>
          ) : (
            <StudentCells rows={rows} view={view} tasks={tasks} nb={nb} me={me} onReply={reply} />
          )}
        </div>
        <aside className="flex flex-col gap-3.5 border-bd px-[18px] py-5 lg:border-l">
          <StudentVerdict view={view} latest={latest} />
          <AutochecksCard view={view} />
          <AllComments view={view} />
          <HistoryCard list={list} view={view} />
        </aside>
      </div>
    </Shell>
  );
}
